import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttachmentFiles } from '../../attachments/AttachmentFiles.js';
import { loadReportFiles, type ReportFiles } from '../../attachments/report-files.js';
import { ZIP_TYPE } from '../fuel/period-package.js';
import { browserPdfOutput, pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import type { RecordPdfT } from '../reports/record-pdf.js';
import { downloadFile } from '../reports/xlsx.js';
import type { DrillRecordSource } from './record-source.js';
import type { DrillRecordDetail } from './record-types.js';
import {
  type DrillReportInput,
  drillReportFileName,
  drillReportPdf,
  drillReportsPdf,
  drillReportsZip,
} from './report-pdf.js';

/**
 * Tatbikat raporunun dışa aktarımı (ADR 0128 Faz 5 Ek): tek kaydı PDF olarak kaydetme ya da
 * yazdırma, Records'ta seçili kayıtları tek PDF ya da manifestli ZIP olarak verme. PDF istemcide,
 * internetsiz üretilir; marka okunamazsa yalnız şirket adı boş kalır.
 */
export function useDrillReportExport(source: DrillRecordSource) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<null | { done: number; total: number }>(null);
  const tr: RecordPdfT = (key, options) => String(t(key, options ?? {}));
  const brand = async () => (await source.brand?.().catch(() => null)) ?? null;
  const output = () => source.pdf ?? browserPdfOutput();
  const attachmentFiles = useAttachmentFiles();
  /**
   * Eklerin resimleri ve bağlantıları (ADR 0130 §A): adres kaynaktan (web ofis) ya da uygulamanın
   * ek erişiminden (gemi, `felix-file://`). Adres yoksa yalnız adlar yazılır.
   */
  const files = async (record: DrillRecordDetail): Promise<ReportFiles> => {
    const url = source.attachmentUrl ?? attachmentFiles?.url;
    if (!url || !record.attachmentList?.length) return {};
    const base = typeof window === 'undefined' ? null : window.location.href;
    return loadReportFiles(record.attachmentList, { url, base });
  };
  const fail = (error: unknown) => {
    const key = pdfErrorKey(errorCode(error));
    return key === 'restHours.pdf.error' ? t('drillReport.archive.failed') : t(key);
  };
  const inputs = async (ids: readonly string[]): Promise<DrillReportInput[]> => {
    const logo = await brand();
    const printedAt = new Date().toISOString();
    const out: DrillReportInput[] = [];
    for (const id of ids) {
      const record = await source.detail(id);
      out.push({ record, brand: logo, printedAt, files: await files(record) });
      setBusy({ done: out.length, total: ids.length });
    }
    return out;
  };

  /** Tek kayıt: kaydet (`save`) ya da yazdırmaya aç (`print`). Hata metni döner. */
  const single = async (record: DrillRecordDetail, mode: 'save' | 'print') => {
    setBusy({ done: 0, total: 1 });
    try {
      const definition = drillReportPdf(tr, {
        record,
        brand: await brand(),
        printedAt: new Date().toISOString(),
        files: await files(record),
      });
      const name = drillReportFileName(record);
      await output()[mode](name, () => renderPdf(definition, { print: mode === 'print' }));
      return null;
    } catch (error) {
      return fail(error);
    } finally {
      setBusy(null);
    }
  };

  /** Seçili kayıtlar: tek PDF ya da ZIP. Hata metni döner. */
  const bulk = async (ids: readonly string[], format: 'pdf' | 'zip', fileBase: string) => {
    setBusy({ done: 0, total: ids.length });
    try {
      const list = await inputs(ids);
      const generatedAt = new Date().toISOString();
      if (format === 'pdf') {
        const definition = drillReportsPdf(tr, list, generatedAt);
        await output().save(`${fileBase}.pdf`, () => renderPdf(definition));
      } else {
        const { zip } = await drillReportsZip(tr, list, generatedAt);
        if (source.saveFile) await source.saveFile(`${fileBase}.zip`, zip, ZIP_TYPE);
        else downloadFile(`${fileBase}.zip`, zip, ZIP_TYPE);
      }
      return null;
    } catch (error) {
      return fail(error);
    } finally {
      setBusy(null);
    }
  };

  return { busy, single, bulk };
}
